"use client";

// 3-step picker: Location → Section → Category. On confirm, navigates to
// /posts/create/{location}/{category} where the actual post form lives.
//
// Reusable in both a standalone page (/profile/posts/new) and a modal
// (e.g. on /posts/{location} where "Create a post" needs a category-first
// flow). When `initialLocation` is supplied, the location step is
// pre-selected and locked unless `allowLocationChange` is true.

import React from "react";
import { useRouter } from "next/navigation";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ChevronRight, Handshake, Briefcase, Megaphone, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { POST_SECTIONS, categoriesInSection, type PostSection } from "@/lib/posts/sections";

type Metro = { slug: string; name?: string; state?: string };
type Cat = { slug: string; name: string; type?: string };

// The four sections and the rule that fills them live in lib/posts/sections.ts,
// shared with the metro browse page so one category cannot sit under one
// heading here and a different heading there. Icons stay local because the
// shared module is plain data with no React or icon dependency.
const SECTION_ICONS: Record<PostSection, typeof Handshake> = {
  connect: Handshake,
  hiring: Briefcase,
  looking: Megaphone,
  services: Sparkles,
};

export interface PostCategoryPickerProps {
  initialLocation?: string;
  /** When true, the location step is editable; when false (default), an `initialLocation` value locks the step. */
  allowLocationChange?: boolean;
  /** Called instead of router.push when the user confirms. Use to close a modal. */
  onConfirm?: (location: string, categorySlug: string) => void;
  onCancel?: () => void;
  /** Submit button label. Defaults to "Continue". */
  submitLabel?: string;
}

export default function PostCategoryPicker({
  initialLocation,
  allowLocationChange = false,
  onConfirm,
  onCancel,
  submitLabel = "Continue",
}: PostCategoryPickerProps) {
  const router = useRouter();
  const [metros, setMetros] = React.useState<Metro[]>([]);
  const [metrosLoading, setMetrosLoading] = React.useState(true);
  const [stateCode, setStateCode] = React.useState<string>("");
  const [location, setLocation] = React.useState<string>(initialLocation || "");
  const [section, setSection] = React.useState<PostSection | "">("");
  const [category, setCategory] = React.useState<string>("");
  const [cats, setCats] = React.useState<Cat[]>([]);
  const [catsLoading, setCatsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Load metros for the location dropdown (skip if location is locked)
  React.useEffect(() => {
    if (initialLocation && !allowLocationChange) {
      setMetrosLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/search2/facets", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j: any = await res.json();
        const list: Metro[] = Array.isArray(j?.metros) ? j.metros : [];
        if (!cancelled) {
          // Sort alphabetically by display name
          list.sort((a, b) => String(a.name || a.slug).localeCompare(String(b.name || b.slug)));
          setMetros(list);
          // If an initialLocation was supplied and we're allowing changes, seed the state from it
          if (initialLocation) {
            const seed = list.find(m => m.slug === initialLocation);
            if (seed?.state) setStateCode(seed.state);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load locations");
      } finally {
        if (!cancelled) setMetrosLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialLocation, allowLocationChange]);

  // Distinct sorted states from the loaded metros
  const states = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const m of metros) {
      const s = (m.state || "").trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, [metros]);

  // Metros within the selected state
  const metrosInState = React.useMemo(() => {
    if (!stateCode) return [] as Metro[];
    return metros.filter(m => (m.state || "") === stateCode);
  }, [metros, stateCode]);

  // Load categories whenever location changes; reset section + category
  React.useEffect(() => {
    if (!location) {
      setCats([]);
      setSection("");
      setCategory("");
      return;
    }
    let cancelled = false;
    setCatsLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/categories?metro=${encodeURIComponent(location)}`, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list: Cat[] = await res.json();
        if (!cancelled) setCats(Array.isArray(list) ? list : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load categories");
      } finally {
        if (!cancelled) setCatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [location]);

  // When section changes, reset the category since the available list shifts
  React.useEffect(() => {
    setCategory("");
  }, [section]);

  const filteredCats = React.useMemo(
    () => (section ? categoriesInSection(cats, section) : []),
    [cats, section]
  );

  // Slug → "Title Case" fallback for when the metros API hasn't populated a name yet.
  const titleCaseSlug = React.useCallback(
    (slug: string) => slug.replace(/-/g, " ").replace(/\b\w/g, s => s.toUpperCase()),
    []
  );

  const placeName = React.useMemo(() => {
    if (!location) return "";
    const m = metros.find(x => x.slug === location);
    return m?.name || titleCaseSlug(location);
  }, [location, metros, titleCaseSlug]);

  const canSubmit = !!location && !!section && !!category;

  function onSubmit() {
    if (!canSubmit) return;
    if (onConfirm) {
      onConfirm(location, category);
      return;
    }
    router.push(`/posts/create/${encodeURIComponent(location)}/${encodeURIComponent(category)}`);
  }

  // Lock the location step when an initial value was supplied and the caller didn't opt into editing
  const locationLocked = !!initialLocation && !allowLocationChange;

  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-2xl border border-red-300/60 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-200">
          {error}
        </div>
      )}

      {/* 1. Location — split into State → Metro when not locked */}
      {locationLocked ? (
        <div className="space-y-2">
          <Label htmlFor="picker-location" className="text-sm font-medium">Location</Label>
          <div className="flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm text-zinc-700 dark:text-zinc-200">
            {placeName || location}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="picker-state" className="text-sm font-medium">State</Label>
            <Select
              value={stateCode || undefined}
              onValueChange={(v) => { setStateCode(v); setLocation(""); }}
              disabled={metrosLoading}
            >
              <SelectTrigger id="picker-state" className="w-full">
                <SelectValue placeholder={metrosLoading ? "Loading…" : "Choose a state"} />
              </SelectTrigger>
              <SelectContent>
                {states.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="picker-location" className="text-sm font-medium">Metro</Label>
            <Select
              value={location || undefined}
              onValueChange={(v) => setLocation(v)}
              disabled={metrosLoading || !stateCode}
            >
              <SelectTrigger id="picker-location" className="w-full">
                <SelectValue placeholder={
                  metrosLoading
                    ? "Loading…"
                    : !stateCode
                      ? "Pick a state first"
                      : metrosInState.length === 0
                        ? "No metros in this state"
                        : "Choose a metro"
                }>
                  {/* Force the friendly name in the trigger so we never display the raw slug */}
                  {location ? placeName : undefined}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {metrosInState.map(m => (
                  <SelectItem key={m.slug} value={m.slug}>{m.name || titleCaseSlug(m.slug)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* 2. Section */}
      <fieldset className="space-y-2" disabled={!location || catsLoading}>
        <legend className="text-sm font-medium">Section</legend>
        <div
          role="radiogroup"
          aria-label="Section"
          // 2x2 at every width above sm, deliberately not 4-across: this picker also
          // renders inside the metro page create modal, whose DialogContent is
          // sm:max-w-lg. Tailwind breakpoints read the VIEWPORT, not the container, so
          // a lg:grid-cols-4 would put four cards in 512px on any wide screen.
          className="grid grid-cols-1 gap-2 sm:grid-cols-2"
        >
          {POST_SECTIONS.map(s => {
            const selected = section === s.key;
            const disabled = !location || catsLoading;
            const Icon = SECTION_ICONS[s.key];
            return (
              <button
                key={s.key}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setSection(s.key)}
                disabled={disabled}
                className={cn(
                  "flex flex-col items-start gap-1.5 rounded-xl border p-4 text-left transition-colors outline-none",
                  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                  selected
                    ? "border-foreground bg-accent/40"
                    : "border-border hover:border-foreground/40 hover:bg-accent/20",
                  disabled && "pointer-events-none opacity-50"
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                <span className="font-semibold text-sm">{s.label}</span>
                <span className="text-xs text-muted-foreground leading-snug">{s.hint}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* 3. Category */}
      <div className="space-y-2">
        <Label htmlFor="picker-category" className="text-sm font-medium">Category</Label>
        <Select
          value={category || undefined}
          onValueChange={(v) => setCategory(v)}
          disabled={!section || filteredCats.length === 0}
        >
          <SelectTrigger id="picker-category" className="w-full">
            <SelectValue placeholder={
              !section
                ? "Pick a section first"
                : filteredCats.length === 0
                  ? "No categories in this section"
                  : "Choose a category"
            } />
          </SelectTrigger>
          <SelectContent>
            {filteredCats.map(c => (
              <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:items-center">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        )}
        <Button
          type="button"
          variant="primary"
          disabled={!canSubmit}
          onClick={onSubmit}
          className="gap-2"
        >
          <span>{submitLabel}</span>
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
